import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from './lib/api';
import { searchPcg, pcg2026, type PcgAccount } from './lib/pcg2026';

const pcgMap = new Map(pcg2026.map(a => [a.numero, a.libelle]));
import type { Transaction } from './lib/api-types';

interface OperationEditModalProps {
  operation: Transaction;
  entrepriseId: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface OperationLine {
  id: string;
  compte_id: string;
  compte_libelle: string;
  debit_centimes: number;
  credit_centimes: number;
}

const OperationEditModal: React.FC<OperationEditModalProps> = ({
  operation,
  entrepriseId,
  onClose,
  onSuccess,
}) => {

  const [libelle, setLibelle] = useState(operation.libelle);
  const [lines, setLines] = useState<OperationLine[]>(
    operation.lignes && operation.lignes.length > 0
      ? operation.lignes.map((line, index) => ({
          id: String(index + 1),
          compte_id: line.compte_id,
          compte_libelle: pcgMap.get(line.compte_id) ?? '',
          debit_centimes: line.debit_centimes,
          credit_centimes: line.credit_centimes,
        }))
      : [
          { id: '1', compte_id: '', compte_libelle: '', debit_centimes: 0, credit_centimes: 0 },
          { id: '2', compte_id: '', compte_libelle: '', debit_centimes: 0, credit_centimes: 0 },
        ]
  );
  const [compteSearch, setCompteSearch] = useState('');
  const [compteOptions, setCompteOptions] = useState<PcgAccount[]>([]);
  const [searchIndex, setSearchIndex] = useState<number | null>(null);

  const updateMutation = useMutation({
    mutationFn: (data: any) => api.transactions.patch(operation.id, data),
    onSuccess: () => {
      onSuccess();
    },
  });

  useEffect(() => {
    if (compteSearch.length < 2 || searchIndex === null) {
      setCompteOptions([]);
      return;
    }
    setCompteOptions(searchPcg(compteSearch).slice(0, 30));
  }, [compteSearch, searchIndex]);

  const totalDebit = lines.reduce((sum, line) => sum + line.debit_centimes, 0);
  const totalCredit = lines.reduce((sum, line) => sum + line.credit_centimes, 0);
  const isBalanced = totalDebit === totalCredit;

  const addLine = () => {
    const newId = String(lines.length + 1);
    setLines([...lines, { id: newId, compte_id: '', compte_libelle: '', debit_centimes: 0, credit_centimes: 0 }]);
  };

  const updateLine = (id: string, updates: Partial<OperationLine>) => {
    setLines(lines.map(line => line.id === id ? { ...line, ...updates } : line));
  };

  const removeLine = (id: string) => {
    if (lines.length > 1) {
      setLines(lines.filter(line => line.id !== id));
    }
  };

  const handleCompteSelect = (lineId: string, account: PcgAccount) => {
    updateLine(lineId, { compte_id: account.numero, compte_libelle: account.libelle });
    setCompteOptions([]);
    setSearchIndex(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!libelle.trim()) {
      alert('Le libellé est obligatoire');
      return;
    }

    if (!isBalanced) {
      alert('Les débits et crédits doivent être équilibrés');
      return;
    }

    if (lines.some(line => !line.compte_id)) {
      alert('Tous les comptes PCG doivent être remplis');
      return;
    }

    const payload = {
      entreprise_id: entrepriseId,
      libelle: libelle.trim(),
      lignes: lines.map(line => ({
        compte_id: line.compte_id,
        debit_centimes: Math.round(line.debit_centimes),
        credit_centimes: Math.round(line.credit_centimes),
      })),
    };

    updateMutation.mutate(payload);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Modifier l'opération</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 pt-6 pb-52 overflow-y-auto max-h-96">
            <div className="space-y-5">
              {/* LIBELLÉ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Libellé</label>
                <input
                  type="text"
                  value={libelle}
                  onChange={(e) => setLibelle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                  required
                />
              </div>

              {/* OPERATION TABLE */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Lignes d'écriture</label>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-1/2">
                          Compte PCG
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                          Débit (€)
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                          Crédit (€)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, index) => (
                        <tr key={line.id} className="border-t border-gray-100">
                          <td className="px-4 py-3">
                            <div className="relative">
                              <input
                                type="text"
                                value={compteSearch && searchIndex === index ? compteSearch : `${line.compte_id} ${line.compte_libelle ? '— ' + line.compte_libelle : ''}`.trim()}
                                onChange={(e) => {
                                  setCompteSearch(e.target.value);
                                  setSearchIndex(index);
                                }}
                                onFocus={() => setSearchIndex(index)}
                                placeholder="Rechercher compte..."
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                              />
                              {compteOptions.length > 0 && searchIndex === index && (
                                <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                  {compteOptions.map((acc) => (
                                    <li
                                      key={acc.numero}
                                      onClick={() => handleCompteSelect(line.id, acc)}
                                      className="px-4 py-2 text-sm hover:bg-purple-50 cursor-pointer border-b border-gray-50 last:border-0"
                                    >
                                      <strong>{acc.numero}</strong> — {acc.libelle}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              step="1"
                              min="0"
                              value={line.debit_centimes || ''}
                              onChange={(e) => updateLine(line.id, { debit_centimes: Math.round(parseInt(e.target.value) || 0) })}
                              className="w-full px-3 py-2 text-sm text-right border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 pr-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              placeholder="0"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              step="1"
                              min="0"
                              value={line.credit_centimes || ''}
                              className="w-full px-3 py-2 text-sm text-right border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 pr-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              placeholder="0"
                              onChange={(e) => updateLine(line.id, { credit_centimes: Math.round(parseInt(e.target.value) || 0) })}
                            />
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t border-gray-200 bg-gray-50">
                        <td className="px-4 py-3 text-left">
                          {isBalanced ? (
                            <span className="text-green-600 font-bold text-sm">ÉQUILIBRÉE</span>
                          ) : (
                            <span className="text-red-600 font-bold text-sm">DÉSÉQUILIBRÉE</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">
                          €{((totalDebit / 100)).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">
                          €{((totalCredit / 100)).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 flex justify-between">
                  <button
                    type="button"
                    onClick={addLine}
                    className="px-3 py-2 bg-purple-100 text-purple-700 text-sm font-medium rounded-lg hover:bg-purple-200 transition-colors"
                  >
                    + Ajouter
                  </button>
                  {lines.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLine(lines[lines.length - 1].id)}
                      className="px-3 py-2 bg-red-100 text-red-700 text-sm font-medium rounded-lg hover:bg-red-200 transition-colors"
                    >
                      × Supprimer
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending || !isBalanced}
              className={`px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors ${
                isBalanced
                  ? 'bg-purple-600 hover:bg-purple-700'
                  : 'bg-gray-400'
              }`}
            >
              {updateMutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OperationEditModal;